import { useEffect, useState } from "react";
import { fetchAccountingSummary } from "../../../api/company";
import { useTranslation } from "react-i18next";
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from "@heroicons/react/24/outline";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

export const AccountingSummarySection = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadSummary();
  }, [selectedYear]);

  const loadSummary = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAccountingSummary(selectedYear);
      if (result.error) {
        setError(result.data?.errors?.[0] || t("accounting.summary.load_error"));
      } else {
        setSummary(result.data);
      }
    } catch (err) {
      setError(t("accounting.summary.load_error"));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate available years (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const availableYears = [];
  for (let year = currentYear; year >= currentYear - 5; year--) {
    availableYears.push(year);
  }
  
  // Prepare monthly data for recharts
  const prepareMonthlyChartData = () => {
    if (!summary?.monthly) return [];
    
    const monthNames = [
      t("accounting.summary.months.january"),
      t("accounting.summary.months.february"),
      t("accounting.summary.months.march"),
      t("accounting.summary.months.april"),
      t("accounting.summary.months.may"),
      t("accounting.summary.months.june"),
      t("accounting.summary.months.july"),
      t("accounting.summary.months.august"),
      t("accounting.summary.months.september"),
      t("accounting.summary.months.october"),
      t("accounting.summary.months.november"),
      t("accounting.summary.months.december"),
    ];
    
    const chartData = [];
    
    for (let month = 1; month <= 12; month++) {
      const monthData = summary.monthly[month];
      if (monthData) {
        const income = parseFloat(monthData.income?.total || 0);
        const cost = parseFloat(monthData.costs?.total || 0);
        const result = parseFloat(monthData.profit || 0);
        
        chartData.push({
          month: monthNames[month - 1],
          income: income,
          cost: cost,
          result: result,
        });
      }
    }
    
    return chartData;
  };
  
  const monthlyChartData = prepareMonthlyChartData();

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency: "NOK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  if (isLoading) {
    return (
      <div className="rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-5 w-40 rounded bg-slate-200 mb-4"></div>
            <div className="h-32 rounded-2xl bg-slate-100"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  // Ensure values are numbers
  const profit = parseFloat(summary.profit || 0);
  // Convert profit_margin to number (it may come as string from API)
  const profitMargin = typeof summary.profit_margin === 'string' 
    ? parseFloat(summary.profit_margin) 
    : (summary.profit_margin || 0);

  // Break down costs - supplier invoices and item_lists
  const supplierInvoiceCosts = parseFloat(summary.costs?.supplier_invoices || 0);
  const itemListCosts = parseFloat(summary.costs?.item_lists || 0);

  // Break down income - only customer invoices
  const customerInvoiceIncome = parseFloat(summary.income?.customer_invoices || 0);

  // Expected report income data
  const expectedReportIncome = parseFloat(summary.expected_report_income?.income || 0);
  const expectedReportCosts = parseFloat(summary.expected_report_income?.costs || 0);
  const expectedReportProfit = parseFloat(summary.expected_report_income?.profit || 0);

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="p-6">
            {/* Header */}
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                  <ChartBarIcon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Regnskap
                  </div>
                  <h3 className="mt-1 text-xl font-semibold text-slate-950">
                    {t("accounting.summary.title")}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {summary.calculated_at
                      ? new Date(summary.calculated_at).toLocaleDateString("nb-NO", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : ""}
                  </p>
                </div>
              </div>
              {/* Year Selector */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-600">
                  {t("accounting.summary.year") || "Year"}:
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-0"
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Summary Numbers and Chart */}
            <div className="mb-6">
              <div className="flex items-start gap-6">
                {/* Chart on the left */}
                <div className="flex-1">
                  {/* Monthly Chart */}
                  {monthlyChartData && monthlyChartData.length > 0 ? (
                    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-slate-900">
                          {t("accounting.summary.monthly_overview")}
                        </h4>
                        <p className="mt-1 text-xs text-slate-500">
                          {t("accounting.summary.chart_description")}
                        </p>
                      </div>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            data={monthlyChartData}
                            margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="month" 
                              tick={{ fill: '#64748b', fontSize: 12 }}
                              stroke="#9ca3af"
                            />
                            <YAxis 
                              tick={{ fill: '#64748b', fontSize: 12 }}
                              stroke="#9ca3af"
                              tickFormatter={(value) => {
                                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                                if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                                return value.toString();
                              }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1f2937',
                                border: 'none',
                                borderRadius: '8px',
                                color: '#f3f4f6',
                              }}
                              formatter={(value, name) => {
                                if (typeof value === "number") {
                                  const formatted = formatCurrency(value);
                                  const labelMap = {
                                    income: t("accounting.summary.income"),
                                    cost: t("accounting.summary.costs"),
                                    result: t("accounting.summary.profit"),
                                  };
                                  return [formatted, labelMap[name] || name];
                                }
                                return [value, name];
                              }}
                              labelStyle={{ color: '#f3f4f6', fontWeight: 'bold', marginBottom: '4px' }}
                            />
                            <Legend 
                              wrapperStyle={{ paddingTop: '20px' }}
                              iconType="rect"
                            />
                            {/* Income bar */}
                            <Bar 
                              dataKey="income" 
                              name={t("accounting.summary.income")}
                              fill="#10b981" 
                              radius={[4, 4, 0, 0]}
                              barSize={18}
                            />
                            {/* Cost bar */}
                            <Bar 
                              dataKey="cost" 
                              name={t("accounting.summary.costs")}
                              fill="#ef4444" 
                              radius={[4, 4, 0, 0]}
                              barSize={18}
                            />
                            {/* Result line (income - cost) */}
                            <Line
                              type="monotone"
                              dataKey="result"
                              name={t("accounting.summary.profit")}
                              stroke="#8b5cf6"
                              strokeWidth={2}
                              dot={{ fill: '#8b5cf6', r: 4 }}
                              activeDot={{ r: 6, fill: '#7c3aed' }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 py-12 text-center text-slate-500">
                      <p className="text-sm">{t("accounting.summary.no_monthly_data")}</p>
                    </div>
                  )}
                </div>

                {/* Numbers on the right */}
                <div className="flex flex-col gap-4 min-w-[220px]">
                  {/* Profit */}
                  <div className="rounded-[1.5rem] border border-slate-200 bg-slate-950 p-4 text-white">
                    <div className="flex items-center gap-2 mb-1">
                      {profit >= 0 ? (
                        <ArrowTrendingUpIcon className="h-5 w-5 text-emerald-300" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-5 w-5 text-red-300" />
                      )}
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                        {t("accounting.summary.profit")}
                      </span>
                    </div>
                    <div
                      className={`text-2xl font-bold mb-1 ${
                        profit >= 0 ? "text-white" : "text-red-200"
                      }`}
                    >
                      {formatCurrency(profit)}
                    </div>
                    <div className="text-xs text-slate-300">
                      {profitMargin >= 0 ? "+" : ""}
                      {profitMargin.toFixed(1)}% {t("accounting.summary.profit_margin")}
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div className="space-y-2 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {t("accounting.summary.costs")}
                    </div>
                    {supplierInvoiceCosts > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">
                          {t("accounting.summary.supplier_invoices")}
                        </span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(supplierInvoiceCosts)}
                        </span>
                      </div>
                    )}
                    {itemListCosts > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">
                          {t("accounting.summary.item_lists")}
                        </span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(itemListCosts)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Income Breakdown */}
                  <div className="space-y-2 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {t("accounting.summary.income")}
                    </div>
                    {customerInvoiceIncome > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">
                          {t("accounting.summary.customer_invoices")}
                        </span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(customerInvoiceIncome)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Expected Report Income */}
                  {(expectedReportIncome > 0 || expectedReportCosts > 0) && (
                    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <ChartBarIcon className="h-5 w-5 text-slate-700" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {t("accounting.summary.expected_report_income")}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">
                            {t("accounting.summary.expected_income")}
                          </span>
                          <span className="font-medium text-slate-900">
                            {formatCurrency(expectedReportIncome)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">
                            {t("accounting.summary.expected_costs")}
                          </span>
                          <span className="font-medium text-slate-900">
                            {formatCurrency(expectedReportCosts)}
                          </span>
                        </div>
                        <div className="pt-2 border-t border-slate-200">
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-semibold text-slate-700">
                              {t("accounting.summary.expected_profit")}
                            </span>
                            <span
                              className={`text-sm font-bold ${
                                expectedReportProfit >= 0 ? "text-green-600" : "text-red-600"
                              }`}
                            >
                              {formatCurrency(expectedReportProfit)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
      </div>
    </section>
  );
};

